<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-flex>
        <dt-search-box @typing="searchValue" @reset-datatable="resetDataTable()" @clear-search-field="search = null" />
      </v-flex>
    </v-card>

    <v-card flat>
      <v-layout row>
        <v-layout row wrap justify-start align-end>
          <v-flex pa-2>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                    style="margin:0 10px 0; height: 30px; width: 30px;"
                    :loading="loadThePrintBtn"
                    flat
                    fab
                    v-on="on"
                    color="primary"
                    @click="getNoPaginatedData('print')"
                >
                  <v-icon>print</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Print Table`) }}</span>
            </v-tooltip>
            <v-tooltip top>
              <template v-slot:activator="{ on }">
                <v-btn
                    style="margin: 0; height: 30px; width: 30px"
                    fab
                    :loading="loadTheExportBtn"
                    flat
                    v-on="on"
                    color="primary"
                    @click="getNoPaginatedData('export')"
                >
                  <v-icon>fa-file-excel</v-icon>
                </v-btn>
              </template>
              <span>{{ $t(`Export Table`) }}</span>
            </v-tooltip>
          </v-flex>
        </v-layout>

        <v-layout row justify-end>
          <v-flex shrink pa-2>
            <v-btn dark color="primary" v-if="hasPermissions(['outlets_store'])" @click="dialogs.addDialog = true">
              <v-icon>add</v-icon>
              {{ $t('datatable-add-new') }}
            </v-btn>
          </v-flex>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
          :headers="headers"
          :items="resultData.data"
          :item-key="defaultSortBy"
          :search="search"
          :loading="loading"
          :pagination.sync="pagination"
          :rows-per-page-items="pagination.rowsPerPageItems"
          :total-items="resultData.total"
          :no-data-text="$t('No available data')"
          hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.outlet_id }}</td>-->
          <td>{{ props.item.outlet_name }}</td>
          <td>{{ props.item.outlet_phone }}</td>
          <td>{{ props.item.outlet_email }}</td>
          <td>{{ props.item.is_active }}</td>
          <td>
            <v-tooltip top>
              <v-chip
                  slot="activator"
                  label
                  :color="props.item.is_active === 'Active' ? 'success' : 'error'"
                  text-color="white"
                  class="no-select"
              >
                <v-icon>{{ props.item.is_active === 'Active' ? 'check_circle' : 'block' }}</v-icon>
              </v-chip>
              <span>{{ props.item.is_active === 'Active' ? $t('Active') : $t('Inactive') }}</span>
            </v-tooltip>
          </td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['outlets_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.outlet_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-view') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('View Outlet Information') }}</span>
                </v-tooltip>

                <v-tooltip v-if="hasPermissions(['outlets_update'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="editRecord(props.item.outlet_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">{{ $t('datatable-edit') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Edit Outlet Information') }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['outlets_destroy'])" />
                <v-tooltip v-if="hasPermissions(['outlets_destroy'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.outlet_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">{{ $t('datatable-delete') }}</v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Outlet Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">{{
            $t('Sorry, No results found for your search criteria')
          }}</v-alert>
      </v-data-table>

      <dt-pagination
          :resultData="resultData"
          :pagination="pagination"
          :perPageNumber="perPageNumber"
          @page-changed="pageChanged"
          @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <!-- %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%% Dialogs %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%% -->

    <view-outlet :viewDialog="dialogs.viewDialog" @hidemodal="dialogs.viewDialog = false" />

    <add-outlet
        :addDialog="dialogs.addDialog"
        @reset-datatable="resetDataTable(), resetAllErrors(true)"
        @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
    />

    <edit-outlet
        :editDialog="dialogs.editDialog"
        @updated="updateDatatableResult(), (dialogs.editDialog = false), (allowGettingData = false)"
        @hidemodal="resetAllErrors(true), (dialogs.editDialog = false)"
    />

    <confirm-deletion
        :deleteDialog="dialogs.deleteDialog"
        :btnDeleteLoading="btnDeleteLoading"
        @hidemodal="dialogs.deleteDialog = false"
        @confirm="deleteAction()"
    />

    <print-export-component
        :printExportDialog="dialogs.printExportDialog"
        :tableHeader="tableHeader"
        :tableBody="tableBody"
        :clickedButton="clickedButton"
        @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    viewOutlet: () => import('./viewOutlet.vue'),
    addOutlet: () => import('./addOutlet.vue'),
    editOutlet: () => import('./editOutlet.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialog: false,
        addDialog: false,
        editDialog: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      deleteId: 0,
      btnDeleteLoading: false,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'outlet_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'outlet_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Outlet Name'), value: 'outlet_name' },
        { text: this.$t('Phone'), value: 'outlet_phone' },
        { text: this.$t('Email'), value: 'outlet_email' },
        { text: this.$t('Status'), value: 'is_active' }
      ],
      allowGettingData: true,
      staffMemberId: 0,
      staffMemberFullName: ''
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Outlets [Branches]'),
      icon: 'fa-cogs'
    })

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'outlets/getOutletsList'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'outlets/updateOutletsList',
      deleteRecord: 'outlets/deleteOutlet',
      fetchOutletInfo: 'outlets/fetchOutletInfo'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['outlets_show', 'outlets_update', 'outlets_destroy'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('outlets_index', {
            params: { is_paginated: 0, search: this.search }
          })
          .then(response => {
            this.tableBody = response.data.data
            this.loadThePrintBtn = false
            this.loadTheExportBtn = false
            this.dialogs.printExportDialog = true
          })
          .catch(() => {
            this.loadThePrintBtn = false
            this.loadTheExportBtn = false
            this.dialogs.printExportDialog = false
          })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
          .then(() => {
            this.pagination.page = this.resultData.current_page
            this.loading = false
          })
          .catch(() => {
            this.loading = false
          })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        rowsPerPageItems: [5, 10, 20, 50],
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchOutletInfo(id).then(() => {
        this.dialogs.viewDialog = true
      })
    },

    editRecord(id) {
      this.fetchOutletInfo(id).then(() => {
        this.dialogs.editDialog = true
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
          .then(() => {
            this.deleteId = 0
            this.btnDeleteLoading = false
            this.dialogs.deleteDialog = false
            let payload = { ...this.pagination, search: this.search }
            this.updateResult(payload)
          })
          .catch(() => {
            this.deleteId = 0
            this.btnDeleteLoading = false
            this.dialogs.deleteDialog = false
          })
    }
  }
}
</script>

<style scoped />
